import { useEffect, useRef, useState } from 'preact/hooks'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { SlugInput } from '@/components/common/SlugInput'
import { websApi } from '@/api/webs'
import { slugify, isValidSlugFormat } from '@/utils/slugify'

interface Props {
  isOpen: boolean
  onClose: () => void
  onConfirm: (slug?: string) => void
  loading: boolean
  hasIntegration?: boolean
  workspaceId: string
  /** Slug already stored on the web, if any. */
  storedSlug?: string | null
  /** Public title the suggestion is derived from. Undefined until one is set. */
  publicTitle?: string
  /**
   * True once the web has ever been signed off. The address is fixed from that point, because the
   * BigMarker conference already carries it and nothing can push a correction (ORB-482).
   */
  frozen: boolean
}

/**
 * Sign-off confirmation, and the moment the public address is chosen.
 *
 * The slug lives here rather than in Settings because that is what the team asked for: decided at the
 * end, after the client conversation, not derived silently at creation. It is pre-filled from the
 * title so the common path is one click, but somebody has to look at it — this is the last screen
 * before the address reaches BigMarker and stops being changeable.
 */
export function WebSignOffModal({
  isOpen,
  onClose,
  onConfirm,
  loading,
  hasIntegration,
  workspaceId,
  storedSlug,
  publicTitle,
  frozen,
}: Props) {
  const [slug, setSlug] = useState('')

  /**
   * Mirrors `slug` synchronously so confirm reads the committed value rather than the one captured
   * when the handler was created.
   *
   * `SlugInput` propagates on a 300ms debounce and flushes on blur, and blur queues a state update
   * that has not necessarily re-rendered by the time the click handler runs. Typing and immediately
   * pressing Sign Off could therefore commit the previous value — and this value becomes a permanent
   * public URL pushed to BigMarker, so "usually fine" is not a good enough guarantee (ORB-482).
   */
  const slugRef = useRef('')
  const applySlug = (next: string) => {
    slugRef.current = next
    setSlug(next)
  }

  // Seed each time the panel opens rather than on mount: the title or the stored slug can change
  // while the page is up, and a stale suggestion here would be committed on confirm.
  useEffect(() => {
    if (!isOpen) return
    applySlug(storedSlug || slugify(publicTitle) || '')
  }, [isOpen, storedSlug, publicTitle])

  const collectsSlug = !frozen
  const slugValid = isValidSlugFormat(slug.trim())

  const message = hasIntegration
    ? "Signing off creates this web's event in BigMarker and syncs its details and speakers."
    : 'This action confirms the web is ready.'

  return (
    <ConfirmationModal
      isOpen={isOpen}
      title="Sign Off Web"
      message={message}
      type="info"
      confirmText="Sign Off"
      onConfirm={() => onConfirm(collectsSlug ? slugRef.current.trim() : undefined)}
      onCancel={onClose}
      loading={loading}
      confirmDisabled={collectsSlug && !slugValid}
    >
      {collectsSlug && (
        <div className="mt-4">
          <SlugInput
            label="Public address"
            required
            value={slug}
            onChange={applySlug}
            checkAvailability={async (candidate) => {
              if (candidate === storedSlug) return { available: true }
              const res = await websApi.checkSlug(workspaceId, candidate)
              return res.data ?? { available: false }
            }}
            helperText={
              publicTitle
                ? 'Suggested from the event title. Change it now if you want something else — it is fixed after sign-off.'
                : 'This becomes the public URL for the landing page and the event. It is fixed after sign-off.'
            }
          />
        </div>
      )}
    </ConfirmationModal>
  )
}
